import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Landmark, Wallet, CheckCircle, ExternalLink, Loader2, AlertCircle } from 'lucide-react';
import { usePASClientAuth } from '../context/PASClientAuthContext';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const usd = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const Done = ({ res }) => (
  <div className="text-center" data-testid="claim-success">
    <CheckCircle className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
    <p className="text-white text-xl font-semibold">Check claimed</p>
    <p className="text-slate-400 text-sm mt-2">{res.message}</p>
    <a href={res.explorer_url} target="_blank" rel="noreferrer" data-testid="claim-explorer-link" className="inline-flex items-center gap-2 mt-4 text-emerald-400 underline text-sm">View on the block explorer <ExternalLink className="w-4 h-4" /></a>
  </div>
);

export default function ClaimTreasuryCheck() {
  const { checkId } = useParams();
  const t = useSearchParams()[0].get('t') || '';
  const { token } = usePASClientAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pas-treasury-check/claim/${checkId}`, { params: { t } }).then(r => setInfo(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Claim link is invalid'));
  }, [checkId, t]);
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/pas-banking/overview`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { const w = r.data.linked_wallet; const a = typeof w === 'string' ? w : w?.address; if (a) setTo(v => v || a); }).catch(() => {});
  }, [token]);

  const connect = async () => {
    if (!window.ethereum) { toast.error('MetaMask is not installed in this browser'); return; }
    try { const [a] = await window.ethereum.request({ method: 'eth_requestAccounts' }); setTo(a); }
    catch { toast.error('Wallet connection was rejected'); }
  };
  const claim = async () => {
    setBusy(true);
    try { setRes((await axios.post(`${API}/pas-treasury-check/claim/${checkId}`, { to: to.trim() }, { params: { t } })).data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Claim failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4" data-testid="claim-page">
      <div className="max-w-lg w-full bg-slate-800 border border-slate-700 rounded-2xl p-8">
        {error && <p className="text-red-400 flex items-center gap-2" data-testid="claim-error"><AlertCircle className="w-5 h-5" />{error}</p>}
        {!error && !info && <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto" />}
        {info && (res ? <Done res={res} /> : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <Landmark className="w-8 h-8 text-emerald-400" />
              <div>
                <h1 className="text-white text-xl font-bold">Claim your Treasury Check</h1>
                <p className="text-slate-400 text-sm">No. {info.check_no ?? '-'} · {info.network} · {info.date}</p>
              </div>
            </div>
            <div className="bg-slate-900 rounded-xl p-4 mb-6">
              <p className="text-slate-400 text-xs uppercase tracking-wider">Pay to the order of</p>
              <p className="text-white font-semibold" data-testid="claim-payee">{info.payee?.name || info.payee_address}</p>
              <p className="text-emerald-400 text-2xl font-bold mt-2" data-testid="claim-amount">${usd(info.amount)}</p>
            </div>
            {info.blocked_reason ? <p className="text-amber-300 text-sm" data-testid="claim-blocked">{info.blocked_reason}.</p> : (
              <>
                <label className="block text-sm text-slate-300 mb-2">Send the check to your wallet ({info.network === 'base' ? 'Base' : info.network})</label>
                <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="0x..." data-testid="claim-wallet-input"
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white font-mono text-sm focus:border-emerald-500 outline-none" />
                <button onClick={connect} type="button" data-testid="claim-connect-btn" className="mt-2 text-sm text-emerald-400 flex items-center gap-2 hover:text-emerald-300"><Wallet className="w-4 h-4" /> Use my MetaMask wallet</button>
                <button onClick={claim} disabled={busy || !/^0x[0-9a-fA-F]{40}$/.test(to.trim())} data-testid="claim-submit-btn"
                  className="w-full mt-6 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl py-3 font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Claim check · free
                </button>
                <p className="text-xs text-slate-500 mt-3">Anyone with this link can claim the check once. Network gas is paid by the BETHCOIN Treasury Reserve.</p>
                <p className="text-xs text-amber-200/80 mt-1" data-testid="claim-expires">Claim by {new Date(info.expires_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}.</p>
                <div className="mt-6 pt-6 border-t border-slate-700 flex items-center gap-4" data-testid="claim-pos-qr">
                  <div className="bg-white p-2 rounded-lg shrink-0"><QRCodeSVG value={window.location.href} size={104} /></div>
                  <p className="text-sm text-slate-300">Paying at a store? Show this code at a BETH point of sale. The merchant scans it and the check pays them directly.</p>
                </div>
              </>
            )}
          </>
        ))}
      </div>
    </div>
  );
}
